import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import process from 'process';
import * as Sentry from '@sentry/nextjs';
import { useAnalytics } from 'use-analytics';
import { doUseAuth0UniversalLogin } from '../../constants';
import axiosConfig from '../../store/sagas/axiosConfig';
import CustomAuthProvider from './CustomAuthProvider';
import Auth0ProviderWrapper from './Auth0ProviderWrapper';
import useAxios401Interceptor from '../../hooks/useAxios401Interceptor';
import readSafeError from '../../helpers/readSafeError';

const AuthProvider = ({ children }) => {
  const router = useRouter();
  const { test } = router.query;
  const development = process && process.env && process.env.NODE_ENV === 'development';
  const { track } = useAnalytics();

  useAxios401Interceptor(router, track);

  // Make sure multiple handlers not added between page change
  const doHaveHandlers =
    axiosConfig.interceptors.response.handlers && axiosConfig.interceptors.response.handlers.length;
  if (!doHaveHandlers) {
    axiosConfig.interceptors.response.use(undefined, (error) => {
      try {
        // We get "Network Error" response when backend service is not available
        if (
          error &&
          typeof error === 'object' &&
          error.message &&
          (error.name === 'Error' || error.name === 'AxiosError') &&
          error.message === 'Network Error'
        ) {
          track('NETWORK_ERROR', {
            path: router.asPath,
            message: error.message,
            url: error && error.config && error.config.url,
          });

          Sentry.captureException(error, {
            tags: { errorType: 'NETWORK_ERROR' },
            extra: {
              path: router.asPath,
              url: error.config?.url,
            },
          });

          track('NETWORK_ERROR_DETAIL', readSafeError(error, router.asPath));
        } else if (
          error &&
          typeof error === 'object' &&
          error.message &&
          error.status !== 401 &&
          (error.name === 'Error' || error.name === 'AxiosError')
        ) {
          track('UNKNOWN_ERROR', {
            path: router.asPath,
            message: error.message,
            url: error && error.config && error.config.url,
          });

          track('UNKNOWN_ERROR_DETAIL', readSafeError(error, router.asPath));
        }
      } catch (e) {
        console.error('error');
      }
      return Promise.reject(error);
    });
  }

  if (test === 'true' || development) {
    // Setting isTest param to each request to backend.
    axiosConfig.defaults.params = {
      ...axiosConfig.defaults.params,
      isTest: true,
    };
  }

  const ProviderComponent = doUseAuth0UniversalLogin ? Auth0ProviderWrapper : CustomAuthProvider;
  return <ProviderComponent>{children || <></>}</ProviderComponent>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider;
